
/**
 * 黄金瞳上报日志工具说明
 * 以 _ 开头的方法为内部方法不允许外部调用，
 *
 * initPageData  //基础数据初始化
 *
 * 错误异常日志上报：_reportError
 * recordJsErrorLog       //jsError     js错误上报
 * recordCatchErrorLog  //catchError  异常错误上报
 * recordDataErrorLog   //parseError  数据错误上报
 * recordHttpErrorLog   //httpError   请求错误上报
 */

import { isWeex, isReactNative } from '../utils/env';


//上报黄金瞳日志的类型
const StatType = {
  performance: 'performance',
  error: 'error',
};
//错误类型分类，对于jsError的类型，括号内的类型，需要黄金瞳去分析出来。
const ErrorType = {
  jsError: 'jsError', //componentDidCatch 里都属于jsError的一种 (EvalError、RangeError、ReferenceError、SyntaxError、TypeError、URIError)
  httpError: 'httpError', //接口>=400的时候，需要报的日志的类型
  parseError: 'parseError', //数据解析错误需要上报日志的类型，例如：某一个楼层数据为空需要上报日志，可以上报这个类型。(检测必须存在的值，然而接口没返回的这种。)
  catchError: 'catchError', //如果你的业务需要你在catch后，报自己的日志，可以写这个，相当于自定义的了，但是大部分catch可以直接用jsError
};



/**
 * 黄金瞳上报日志工具
 * 注意！！外层引用此工具中的任何函数，都要确定函数内部有没有判断prod环境，只在prod环境才会上报数据
 */
class WeexLogUtil {
  //请求相关
  static _requestHasHttpError = 0;

  //页面相关
  static _pageUrl = '';
  static _pageName = '';
  static _bundleUrl = '';

  //黄金瞳上报的数据，调lib接口（以json对象的形式把数据传递给客户端）传的对象。
  //客户端在拼接上自己的基础数据：system等等，再拼接到gif的地址接口上，上报给黄金瞳。
  static _reportLogData = {
    pageUrl: '',
    statType: '',
    statInfo: '', //JSON 字符串
  };

  static _errorLog = null;

  static _ErrorLogClass = null;

  static initErrorLog(hawkEyeStat){
    if(!this._ErrorLogClass){
      console.log('初始化黄金瞳异常上报异常！');
      return;
    }
    if(this._errorLog instanceof this._ErrorLogClass){
      return this;
    }
    // 初始化错误日志上报sdk，需要传入客户端方法
    this._errorLog = new this._ErrorLogClass(hawkEyeStat)
    return this;
  }

  /**
   * 上报日志，this._errorLog.log 存在每秒2个相同类型的上报的节流，同时报三个非force的error 会拦截掉最后一个。
   * @param {object} options 可选参数
   * @param {boolean} options.isForce 是否强制上报
   */
  static _report(options = {}) {
    if (!this._reportLogData.pageUrl || !this._reportLogData.statType) {
      return console.error(
        `日志上报参数错误：page_url = ${this._reportLogData.pageUrl} ; statType = ${this._reportLogData.statType}`
      );
    }
    //公共参数
    this._reportLogData.platform = isWeex ? 'weex' : 'weex_web';

    //调用上报接口，上报数据 this._reportLogData
    if (this.isProdMode && !isReactNative && this._errorLog && this._errorLog.log) {
      this._errorLog.log(this._reportLogData, options);
    }
  }

  /**
   * 使用基础库的方式上报到黄金瞳，规范也是web端的规范。
   * @param {object} error 错误对象
   * @param {object} options 可选参数
   */
  static _reportForWebError(error,options = {}) {
    if (!this.isProdMode) {
      return;
    }
    if (window && window.youpinLib10 && window.youpinLib10.Analysis) {
      const reportError = window.youpinLib10.Analysis.reportError;
      // 采用自定义description的方式上报，包括了除message的其他自动的信息。message单独一个字段。
      const err = {
        message: error.message,
        description: JSON.stringify({
          ...error,
          message: '',
        }),
      };
      try {
        typeof reportError == 'function' && reportError(err);
      } catch (error) {
        console.log(error);
      }
    }
  }

  /**
   * 上报错误数据到黄金瞳
   * @param {object} error 错误信息对象
   * @param {string} error.type 错误大类型（ErrorType的枚举类型）必需
   * @param {string} error.message 错误信息描述 必需
   * @param {object} error.reqParam 请求错误的请求参数（非必需，请求错误类型必需）
   */
  static _reportError(error, options) {
    if (!error.type || !error.message) {
      //上报的时候必须有错误类型和错误信息
      return;
    }
    //存在httpError的话，其他的非httpError的错误就不上报了
    if (this._requestHasHttpError && error.type !== ErrorType.httpError) {
      return;
    }
    error.pageName = this._pageName;
    error.bundleUrl = this._bundleUrl;
    this._reportLogData.pageUrl = this._pageUrl;
    this._reportLogData.statType = StatType.error;
    this._reportLogData.esiType = error.type; //错误类型放到外面一层。 esiType = errorStatInfoType 错误类型（一级）
    this._reportLogData.statInfo = JSON.stringify(error);

    //weex端采用海南的lib方式统一上报到黄金瞳， 而web端使用的是基础库的lib上报。
    isWeex ? this._report(options) : this._reportForWebError(error);
  }


  /**
   * 获取错误信息的message 拼接，包括堆栈，直接JSON.stringify不行，Error类型。
   * @param {object} e 错误对象
   */
  static _getErrorMes(e) {
    return JSON.stringify(e, Object.getOwnPropertyNames(e));
  }

  /**
   * 初始化黄金瞳上报的基础数据
   * @param {Object} options 页面数据参数
   * @param {String} options.pageName  页面标识
   * @param {String} options.pageUrl  页面地址
   * @param {String} options.bundleUrl bundle包地址
   * @param {boolean} options.isProdMode 是否为线上环境，日志上报只有线上环境才会上报，其他环境不上报
   */
  static initPageData(options) {
    const { pageName = '', pageUrl = '', bundleUrl = '' } = options;
    this._pageName = pageName;
    this._pageUrl = pageUrl;
    this._bundleUrl = bundleUrl;
    this.isProdMode = !!options.isProdMode;

    return this; //返回当前的类，方便链式调用
  }

  /**
   * 记录错误日志，并上报给黄金瞳
   * @param {Object} error 错误信息对象
   * @param {String} error.type 错误信息类型 ErrorType
   * @param {String} error.message 错误信息字符串
   */
  static recordJsErrorLog(error) {
    if (!this.isProdMode || !error) {
      return;
    }
    const message = this._getErrorMes(error); //拼接所有信息
    this._reportError({ type: ErrorType.jsError, message });
    return this;
  }

  /**
   * 上报catch异常信息，recordCatchErrorLog('errorlog',error) 或者 recordCatchErrorLog(error)
   * @param {string | Object} message string时：自定义的拼接到error前面的异常信息，Object时： catch后的error对象。
   * @param {Object} error catch的错误日志对象，只有第一个为string的时候，才需要传第二个参数。
   */
  static recordCatchErrorLog(message, error) {
    //该方法调用的次数比较多，所以加上了try catch 确保工具方法不会die。
    try {
      // 开发环境和st环境不上报异常，只将错误打印出来，方便排查
      if (!this.isProdMode) {
        console.log(message, error);
        return;
      }
      message = message || '';
      let errorMessage = '';

      if (typeof message == 'string') {
        errorMessage += message;
      } else if (typeof message == 'object') {
        const e = message;
        errorMessage += this._getErrorMes(e);
        //catch的错误为http抛出的，就不用报了。
        if (e.esiType == ErrorType.httpError) {
          return;
        }
      }
      if (typeof error == 'object') {
        errorMessage += this._getErrorMes(error);
        //catch的错误为http抛出的，就不用报了。
        if (error.esiType == ErrorType.httpError) {
          return;
        }
      } else {
        if (!errorMessage) {
          return;
        }
      }
      this._reportError({ type: ErrorType.catchError, message: errorMessage });
    } catch (e) {
      console.log(e);
    }
    return this;
  }

  /**
   * 数据异常需要上报的信息，比如某些数据是必须的，但是接口没有返回。
   * 比如某些楼层的数据，接口不稳定或者后台条件太多，经常没有显示，需要前端看是否接口正常返回。
   * @param {string} message 异常信息
   */
  static recordDataErrorLog(message) {
    if (!this.isProdMode || !message) {
      return;
    }
    this._reportError({ type: ErrorType.parseError, message });
    return this;
  }

  /**
   * 接口请求的时候的错误。
   * @param {object} error 错误的Error对象，http的话大部分都是throw Error一个对象，而且都用了catch。也有个别使用自定义的错误码了：weex-ajax
   * @param {object} reqParam httpError的话需要传请求参数
   *
   * 如果接口报错了，那么页面中的其他的error，基本都是接口没返回数据导致的。
   * 所以接口报错了，其他的错误就不报了。
   */
  static recordHttpErrorLog(error, reqParam) {
    if (!this.isProdMode) {
      return;
    }
    //没传值的时候默认 {} 表示参数为空
    if (!reqParam) {
      reqParam = JSON.stringify({});
    }
    if (!error) {
      return;
    }
    //http 错误的话，如果有traceid ，就取出该值，放到跟message 一级。
    const traceid = error.traceid;
    delete error.traceid;
    const traceidObj = traceid ? { traceid } : {};
    let message = this._getErrorMes(error);
    if (!(error instanceof Error) && error.code) {
      // 个例，使用了自定义的错误码  weex-ajax中
      message = error.code + ' ' + (error.message || '') + ' ' + (error.options || '');
    }
    this._requestHasHttpError = 1;
    this._reportError(
      { type: ErrorType.httpError, message, reqParam, ...traceidObj },
      { isForce: true }
    );
    return this;
  }
}

export { ErrorType,WeexLogUtil };
